import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { searchProducts } from '../lib/products';
import { useProducts } from '../hooks/useProducts';
import { fetchStorefrontCategories } from '../utils/category';
import { cssBackgroundImage, getProductImageUrl } from '../utils/imageUrl';
import { SPORTS, sportToSlug } from '../utils/sports';
import './SportsNav.css';

function IconSearch() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function SportsNav({ variant = 'editorial' }) {
  const [categories, setCategories] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { products } = useProducts();
  const { formatMoney } = useLocale();

  useEffect(() => {
    fetchStorefrontCategories()
      .then(setCategories)
      .catch(() => {
        setCategories(
          SPORTS.map((sport, index) => ({
            id: sport.slug,
            name: sport.name,
            slug: sport.slug,
            display_order: index + 1,
          }))
        );
      });
  }, []);

  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setDropdownOpen(false);
        if (!searchQuery.trim()) setSearchOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchQuery]);

  // Prefer canonical SPORTS list so homepage always shows "Formula Series", etc.
  const sports = useMemo(() => {
    const fromConfig = SPORTS.map(sport => ({
      id: sport.slug,
      name: sport.name,
      slug: sport.slug,
    }));

    if (!categories.length) return fromConfig;

    // Keep admin-only extra sports, but replace legacy F1 with Formula Series.
    const extras = categories
      .map(category => ({
        id: category.id || category.slug || category.name,
        name: category.name,
        slug: category.slug || sportToSlug(category.name),
      }))
      .filter(category => {
        const slug = category.slug.toLowerCase();
        const name = category.name.toLowerCase();
        if (slug === 'f1' || name === 'f1' || name === 'formula 1') return false;
        return !SPORTS.some(sport => sport.slug === slug);
      });

    return [...fromConfig, ...extras];
  }, [categories]);

  const activeSlug = useMemo(() => {
    const match = location.pathname.match(/^\/sports\/([^/]+)/);
    const slug = match ? match[1].toLowerCase() : '';
    if (slug === 'f1' || slug === 'formula-1') return 'formula-series';
    return slug;
  }, [location.pathname]);

  useEffect(() => {
    if (!activeSlug) return undefined;
    const active = scrollRef.current?.querySelector('.sports-nav-pill.active');
    if (!active) return undefined;
    const id = window.requestAnimationFrame(() => {
      active.scrollIntoView({
        inline: 'center',
        block: 'nearest',
        behavior: 'smooth',
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeSlug, sports]);

  const searchResults = useMemo(
    () => searchProducts(products, searchQuery),
    [products, searchQuery]
  );

  const showDropdown = searchOpen && dropdownOpen && searchQuery.trim().length > 0;

  return (
    <nav className={`sports-nav sports-nav--${variant}`} aria-label="Sports">
      <div className="sports-nav-scroll" ref={scrollRef}>
        {sports.map(sport => {
          const isActive = activeSlug === sport.slug.toLowerCase();
          return (
            <button
              key={sport.id}
              type="button"
              className={`sports-nav-pill${isActive ? ' active' : ''}`}
              onClick={() => navigate(`/sports/${sport.slug}`)}
            >
              {sport.name}
            </button>
          );
        })}
      </div>

      <div className="sports-nav-search" ref={searchWrapRef}>
        <input
          ref={searchInputRef}
          type="search"
          placeholder="Search..."
          className={`sports-nav-search-input${searchOpen ? ' open' : ''}`}
          value={searchQuery}
          onChange={event => {
            setSearchQuery(event.target.value);
            setDropdownOpen(event.target.value.trim().length > 0);
          }}
          onFocus={() => {
            if (searchQuery.trim()) setDropdownOpen(true);
          }}
        />
        <button
          type="button"
          className="sports-nav-icon"
          aria-label="Search products"
          onClick={() => {
            setSearchOpen(open => {
              if (open && !searchQuery.trim()) {
                setDropdownOpen(false);
                return false;
              }
              return true;
            });
          }}
        >
          <IconSearch />
        </button>

        {showDropdown && (
          <div className="sports-nav-dropdown" role="listbox" aria-label="Search results">
            {searchResults.length === 0 ? (
              <p className="sports-nav-empty">No products found.</p>
            ) : (
              searchResults.map(product => (
                <button
                  key={product.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="sports-nav-result"
                  onMouseDown={event => event.preventDefault()}
                  onClick={() => {
                    setSearchQuery('');
                    setDropdownOpen(false);
                    setSearchOpen(false);
                    navigate(`/product/${product.id}`);
                  }}
                >
                  <span
                    className="sports-nav-result-image"
                    style={{
                      background: cssBackgroundImage(getProductImageUrl(product)),
                    }}
                  />
                  <span className="sports-nav-result-info">
                    <span className="sports-nav-result-name">{product.name}</span>
                    <span className="sports-nav-result-price">
                      {formatMoney(
                        product.sale_price != null ? product.sale_price : product.price
                      )}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default SportsNav;
